/** Trimmed API client for CTDCommons.
 *
 * Mirrors the main frontend's api.ts pattern but only exposes the
 * routes the public viewer uses: auth.me, projects.list, and the
 * documents endpoints needed by the Explorer / Viewer / Download.
 *
 * Auth header uses a cached Supabase access token (same trick as the
 * main app) to avoid Web Lock contention during token refresh.
 */

import { supabase } from './supabaseClient'
import { Telemetry } from './telemetry'
import type {
  CommonsUser,
  CompareResult,
  ExplorerResponse,
  Portfolio,
  PreviousVersionResult,
  ViewerSection,
} from '@/types/api'

const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? 'http://localhost:8000'

// --- Token cache (see frontend/src/lib/api.ts for rationale) ----------

let _cachedToken: string | null = null
let _tokenExpiresAt = 0

supabase.auth.onAuthStateChange((_event, session) => {
  if (session?.access_token) {
    _cachedToken = session.access_token
    _tokenExpiresAt = session.expires_at ?? 0
  } else {
    _cachedToken = null
    _tokenExpiresAt = 0
  }
})

async function authHeaders(): Promise<Record<string, string>> {
  const now = Math.floor(Date.now() / 1000)
  if (_cachedToken && _tokenExpiresAt > now + 30) {
    return { Authorization: `Bearer ${_cachedToken}` }
  }
  try {
    const result = await Promise.race([
      supabase.auth.getSession(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('getSession timeout')), 5000),
      ),
    ])
    const session = result.data.session
    if (session?.access_token) {
      _cachedToken = session.access_token
      _tokenExpiresAt = session.expires_at ?? 0
      return { Authorization: `Bearer ${session.access_token}` }
    }
  } catch {
    // Fall through — return empty headers, backend will 401 if needed
  }
  return {}
}

// --- snake_case → camelCase recursive converter -----------------------

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
}

function convertKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) return obj.map(convertKeys)
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([k, v]) => [
        snakeToCamel(k),
        convertKeys(v),
      ]),
    )
  }
  return obj
}

// --- typed errors -----------------------------------------------------

/**
 * A2F-636: thrown when the backend rejects a request with
 * `{error: "commons_access_denied"}` (see `TenantMiddleware._commons_denied`).
 *
 * Exists so `AuthContext` can branch on the error class rather than
 * substring-matching `err.message`, which would silently break if the
 * backend error body shape, log redaction, or i18n layer ever changes.
 */
export class CommonsDeniedError extends Error {
  readonly code = 'commons_access_denied' as const
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'CommonsDeniedError'
    this.status = status
    // Preserve instanceof across transpile targets that break Error
    // subclassing (downlevel emit, some bundler edges). Zero-cost on
    // modern targets — Vite's default esbuild emit doesn't need it,
    // but it's defensive against config drift.
    Object.setPrototypeOf(this, CommonsDeniedError.prototype)
  }
}

// --- fetch wrapper ----------------------------------------------------

/** Capture slow backend responses to PostHog (>500ms only). */
function captureSlowResponse(res: Response, path: string, method: string): void {
  const timing = res.headers.get('X-Response-Time')
  if (!timing) return
  const ms = parseInt(timing, 10)
  if (ms > 500) {
    Telemetry.apiResponseSlow({
      path,
      method: method || 'GET',
      duration_ms: ms,
      status_code: res.status,
    })
  }
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const headers = await authHeaders()
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
      ...options?.headers,
    },
  })
  captureSlowResponse(res, path, options?.method ?? 'GET')
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    // A2F-636: surface commons-denied 403s as a typed error so callers
    // can branch on `instanceof CommonsDeniedError` instead of doing a
    // substring match on the generic error message.
    const parsed = parseJsonBody(body)
    if (
      res.status === 403 &&
      parsed !== null &&
      (parsed as { error?: unknown }).error === 'commons_access_denied'
    ) {
      const msg = (parsed as { message?: unknown }).message
      throw new CommonsDeniedError(
        typeof msg === 'string' ? msg : 'Commons access denied',
        res.status,
      )
    }
    throw new Error(`API error ${res.status}: ${body}`)
  }
  const data = await res.json()
  return convertKeys(data) as T
}

/** Safe JSON.parse — returns null for empty / malformed bodies. */
function parseJsonBody(body: string): unknown {
  if (!body) return null
  try {
    return JSON.parse(body)
  } catch {
    return null
  }
}

// --- download helper --------------------------------------------------

/**
 * Download a document to disk via the backend's /download endpoint.
 *
 * Uses a Blob URL + programmatic <a download> click rather than a plain
 * window.open() so the browser honours the backend's Content-Disposition
 * header and so the request carries the Supabase bearer token.
 */
export async function downloadDocument(
  documentId: string,
  fallbackFilename: string,
): Promise<void> {
  const headers = await authHeaders()
  const res = await fetch(`${API_BASE}/api/v1/documents/${documentId}/download`, {
    headers,
  })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`Download failed (${res.status}): ${body}`)
  }

  const disposition = res.headers.get('content-disposition') || ''
  const filename = parseFilename(disposition) || fallbackFilename

  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Parse a filename from an RFC 6266 Content-Disposition header. */
function parseFilename(disposition: string): string | null {
  // Prefer the UTF-8 form (filename*=UTF-8''...)
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1])
    } catch {
      // fall through
    }
  }
  const asciiMatch = disposition.match(/filename="([^"]+)"/i)
  if (asciiMatch) return asciiMatch[1]
  return null
}

// --- public API -------------------------------------------------------

export const api = {
  getAuthHeaders: authHeaders,

  auth: {
    me: () => apiFetch<CommonsUser | null>('/api/v1/auth/me'),
  },

  portfolios: {
    list: () => apiFetch<Portfolio[]>('/api/v1/projects'),
  },

  documents: {
    explorer: (projectId: string) =>
      apiFetch<ExplorerResponse>(`/api/v1/documents/explorer/${projectId}`),

    viewerSections: (projectId: string, submission?: string) => {
      const qs = submission ? `?submission=${encodeURIComponent(submission)}` : ''
      return apiFetch<ViewerSection[]>(`/api/v1/documents/viewer/${projectId}${qs}`)
    },

    renderFragment: async (documentId: string): Promise<string> => {
      const headers = await authHeaders()
      const res = await fetch(
        `${API_BASE}/api/v1/documents/${documentId}/render?fragment=true`,
        { headers },
      )
      if (!res.ok) throw new Error(`Render failed: ${res.status}`)
      return res.text()
    },

    compare: (originalId: string, modifiedId: string) =>
      apiFetch<CompareResult>(
        `/api/v1/documents/compare?original=${originalId}&modified=${modifiedId}`,
      ),

    previousVersion: (documentId: string, projectId: string) =>
      apiFetch<PreviousVersionResult>(
        `/api/v1/documents/${documentId}/previous?project_id=${projectId}`,
      ),

    renderUrl: (documentId: string) =>
      `${API_BASE}/api/v1/documents/${documentId}/render`,

    download: downloadDocument,
  },
}
